import React, { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import {
    unwrapPrivateKeyToCryptoKey,
    decryptPasswordWithCryptoKey,
    deriveWrappingKey,
    base64ToArrayBuffer
} from '../helpers/cryptoHelper';

/**
 * SECURITY NOTE:
 * To enable non-repetitive admin delegation and wrapping (e.g. creating/resetting admins without constant prompts),
 * the vault private key exists transiently as a raw PKCS8 ArrayBuffer (`privateKeyPkcs8`) in JS memory.
 * While `privateKey` itself is a non-extractable CryptoKey, keeping the raw bytes in JS state represents a pragmatic
 * tradeoff for POS backoffice environments. The PKCS8 buffer is destroyed immediately upon manual lock, tab backgrounding, or timeout.
 */
interface VaultSession {
    privateKey: CryptoKey | null;
    privateKeyPkcs8: ArrayBuffer | null;
    expiresAt: number | null;
}

interface UserData {
    username: string;
    esc_password_encrypted?: string;
    user_id: number;
    [key: string]: any;
}

interface ModalConfig {
    title: string;
    subtitle: string;
    resolve: (password: string) => void;
    reject: (err: any) => void;
    verify?: (password: string) => Promise<void>;
}

interface VaultSessionContextType {
    vaultSession: VaultSession;
    isVaultSessionActive: () => boolean;
    unlockVault: (password: string, userKeys: {
        vault_private_key_wrapped: string;
        vault_private_key_nonce: string;
        crypto_salt: string;
    }) => Promise<{ privateKey: CryptoKey; privateKeyPkcs8: ArrayBuffer }>;
    lockVault: (autoLocked?: boolean) => void;
    refreshVaultTimeout: () => void;
    decryptedPasswords: { [userId: number]: string };
    setDecryptedPasswords: React.Dispatch<React.SetStateAction<{ [userId: number]: string }>>;
    decryptUserPassword: (user: UserData, customPrivateKey?: CryptoKey) => Promise<string>;
    confirmPassword: (title?: string, subtitle?: string, verify?: (password: string) => Promise<void>) => Promise<string>;
}

const VaultSessionContext = createContext<VaultSessionContextType | null>(null);

export const VaultSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [vaultSession, setVaultSession] = useState<VaultSession>({
        privateKey: null,
        privateKeyPkcs8: null,
        expiresAt: null
    });
    const [decryptedPasswords, setDecryptedPasswords] = useState<{ [userId: number]: string }>({});
    const [showUnlockModal, setShowUnlockModal] = useState<boolean>(false);
    const [unlockPassword, setUnlockPassword] = useState<string>("");
    const [unlockError, setUnlockError] = useState<string>("");
    const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
    const [verifying, setVerifying] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const lockTimeoutRef = useRef<any>(null);
    const passwordHideTimeoutsRef = useRef<{ [userId: number]: any }>({});
    const backgroundTimeRef = useRef<number | null>(null);

    const isVaultSessionActive = (): boolean => {
        if (!vaultSession.privateKey || !vaultSession.expiresAt) return false;
        return Date.now() < vaultSession.expiresAt;
    };

    const lockVault = (autoLocked = false) => {
        // Was there actually a session to lock? The tab-blur watcher below fires
        // unconditionally on every refocus after 10+ minutes hidden, regardless of
        // whether the vault was ever unlocked — without this check, every admin
        // who just has this tab open in the background gets a false "session
        // locked" toast on every alt-tab, whether or not they used the vault.
        const wasActive = !!vaultSession.privateKey;

        setVaultSession({ privateKey: null, privateKeyPkcs8: null, expiresAt: null });
        setDecryptedPasswords({});

        // Clear all individual auto-hide timers
        Object.values(passwordHideTimeoutsRef.current).forEach(clearTimeout);
        passwordHideTimeoutsRef.current = {};

        if (lockTimeoutRef.current) {
            clearTimeout(lockTimeoutRef.current);
            lockTimeoutRef.current = null;
        }

        if (autoLocked && wasActive) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 4000,
                timerProgressBar: true,
                icon: 'warning',
                title: 'เซสชันความปลอดภัยถูกล็อก',
                text: 'กรุณายืนยันรหัสผ่านเพื่อเข้าถึงข้อมูลอีกครั้ง'
            });
        }
    };

    const refreshVaultTimeout = () => {
        if (!vaultSession.privateKey) return;
        
        // Reset the timeout ref
        if (lockTimeoutRef.current) {
            clearTimeout(lockTimeoutRef.current);
        }
        lockTimeoutRef.current = setTimeout(() => {
            lockVault(true); // Lock automatically on timeout expiration
        }, 5 * 60 * 1000);

        const newExpiry = Date.now() + 5 * 60 * 1000;
        setVaultSession(prev => ({
            ...prev,
            expiresAt: newExpiry
        }));
    };

    const unlockVault = async (
        password: string,
        userKeys: {
            vault_private_key_wrapped: string;
            vault_private_key_nonce: string;
            crypto_salt: string;
        }
    ): Promise<{ privateKey: CryptoKey; privateKeyPkcs8: ArrayBuffer }> => {
        const { vault_private_key_wrapped, vault_private_key_nonce, crypto_salt } = userKeys;
        
        // 1. Derive wrapping key from password
        const wrappingKey = await deriveWrappingKey(password, crypto_salt);

        // 2. Unwrap the private key to a raw PKCS8 ArrayBuffer
        const ciphertext = base64ToArrayBuffer(vault_private_key_wrapped);
        const nonce = base64ToArrayBuffer(vault_private_key_nonce);
        const privateKeyPkcs8 = await window.crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: new Uint8Array(nonce),
            },
            wrappingKey,
            ciphertext
        );

        // 3. Import as non-extractable CryptoKey for decryption
        const privateKey = await window.crypto.subtle.importKey(
            'pkcs8',
            privateKeyPkcs8.slice(0),
            {
                name: 'ECDH',
                namedCurve: 'P-256',
            },
            false, // non-extractable (extractable: false)
            ['deriveKey']
        );

        const newExpiry = Date.now() + 5 * 60 * 1000;
        setVaultSession({
            privateKey,
            privateKeyPkcs8,
            expiresAt: newExpiry
        });

        if (lockTimeoutRef.current) {
            clearTimeout(lockTimeoutRef.current);
        }
        lockTimeoutRef.current = setTimeout(() => {
            lockVault(true);
        }, 5 * 60 * 1000);

        return { privateKey, privateKeyPkcs8 };
    };

    const decryptUserPassword = async (user: UserData, customPrivateKey?: CryptoKey): Promise<string> => {
        const pk = customPrivateKey || vaultSession.privateKey;
        if (!pk) {
            throw new Error("Vault is locked");
        }
        if (!user.esc_password_encrypted) {
            throw new Error("User has no escrowed password");
        }

        const esc = JSON.parse(user.esc_password_encrypted);
        if (!esc.cipher || !esc.nonce || !esc.epk) {
            throw new Error("ข้อมูลคีย์เข้ารหัสไม่สมบูรณ์");
        }
        const plaintext = await decryptPasswordWithCryptoKey(
            esc.cipher,
            esc.nonce,
            esc.epk,
            pk
        );
        setDecryptedPasswords(prev => ({ ...prev, [user.user_id]: plaintext }));
        
        // Refresh session expiration on activity
        refreshVaultTimeout();

        // 30-second individual password auto-hide
        if (passwordHideTimeoutsRef.current[user.user_id]) {
            clearTimeout(passwordHideTimeoutsRef.current[user.user_id]);
        }
        passwordHideTimeoutsRef.current[user.user_id] = setTimeout(() => {
            setDecryptedPasswords(prev => {
                const copy = { ...prev };
                delete copy[user.user_id];
                return copy;
            });
            delete passwordHideTimeoutsRef.current[user.user_id];
        }, 30 * 1000);
        
        return plaintext;
    };

    const confirmPassword = (
        title = "ยืนยันรหัสผ่านเพื่อความปลอดภัย",
        subtitle = "จำเป็นต้องยืนยันตัวตนเพื่อเข้าถึงข้อมูลหรือดำเนินการที่มีความปลอดภัยสูง",
        verify?: (password: string) => Promise<void>
    ): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            setUnlockPassword("");
            setUnlockError("");
            setShowPassword(false);
            setVerifying(false);
            setModalConfig({
                title,
                subtitle,
                resolve,
                reject,
                verify
            });
            setShowUnlockModal(true);
        });
    };

    const handleModalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setUnlockError("");
        if (!unlockPassword || verifying) return;

        if (modalConfig) {
            setVerifying(true);
            try {
                if (modalConfig.verify) {
                    await modalConfig.verify(unlockPassword);
                }
                modalConfig.resolve(unlockPassword);
                setShowUnlockModal(false);
                setModalConfig(null);
                setUnlockPassword("");
                setShowPassword(false);
            } catch (err: any) {
                console.error("Verification failed:", err);
                setUnlockError("รหัสผ่านไม่ถูกต้อง หรือไม่สามารถถอดรหัสกุญแจระบบได้");
            } finally {
                setVerifying(false);
            }
        }
    };

    const handleModalClose = () => {
        if (modalConfig) {
            modalConfig.reject(new Error("User cancelled password confirmation"));
            setModalConfig(null);
        }
        setShowUnlockModal(false);
        setUnlockPassword("");
        setShowPassword(false);
    };

    useEffect(() => {
        const handleLockOnInactivity = () => {
            if (backgroundTimeRef.current !== null) {
                const backgroundDuration = Date.now() - backgroundTimeRef.current;
                // Lock vault if hidden/blurred for more than 10 minutes (600,000 ms)
                if (backgroundDuration > 10 * 60 * 1000) {
                    lockVault(true);
                }
                backgroundTimeRef.current = null;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                if (backgroundTimeRef.current === null) {
                    backgroundTimeRef.current = Date.now();
                }
            } else if (document.visibilityState === 'visible') {
                handleLockOnInactivity();
            }
        };

        const handleBlur = () => {
            if (backgroundTimeRef.current === null) {
                backgroundTimeRef.current = Date.now();
            }
        };

        const handleFocus = () => {
            handleLockOnInactivity();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
            if (lockTimeoutRef.current) {
                clearTimeout(lockTimeoutRef.current);
            }
            Object.values(passwordHideTimeoutsRef.current).forEach(clearTimeout);
        };
    }, [vaultSession.privateKey]);

    return (
        <VaultSessionContext.Provider value={{
            vaultSession,
            isVaultSessionActive,
            unlockVault,
            lockVault,
            refreshVaultTimeout,
            decryptedPasswords,
            setDecryptedPasswords,
            decryptUserPassword,
            confirmPassword
        }}>
            {children}
            <Modal
                show={showUnlockModal}
                onHide={handleModalClose}
                centered
                backdrop="static"
                dialogClassName="modal-dialog modal-dialog-centered mw-500px"
            >
                <Form onSubmit={handleModalSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title className="fw-bold fs-3 text-dark">
                            <i className="fa fa-lock me-2 text-warning" />
                            {modalConfig?.title || "ยืนยันรหัสผ่านเพื่อความปลอดภัย"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="text-muted fs-7 mb-4">
                            {modalConfig?.subtitle || "กรุณากรอกรหัสผ่านเพื่อเข้าถึงข้อมูลหรือดำเนินการที่มีความปลอดภัยสูง"}
                        </div>
                        
                        {unlockError && (
                            <div className="alert alert-danger py-3 px-4 d-flex align-items-center gap-2 mb-4">
                                <i className="fa fa-exclamation-circle fs-4 text-danger" />
                                <span className="fs-7">{unlockError}</span>
                            </div>
                        )}

                        <Form.Group className="mb-2">
                            <Form.Label className="fw-bold fs-7">
                                รหัสผ่านของคุณ <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="input-group">
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    className="form-control-solid"
                                    placeholder="กรอกรหัสผ่านปัจจุบันของคุณ"
                                    value={unlockPassword}
                                    onChange={(e) => {
                                        setUnlockPassword(e.target.value);
                                        if (unlockError) setUnlockError("");
                                    }}
                                    autoFocus
                                    required
                                    disabled={verifying}
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowPassword((p) => !p)}
                                    disabled={verifying}
                                    tabIndex={-1}
                                >
                                    <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                                </Button>
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" size="sm" onClick={handleModalClose} disabled={verifying}>
                            ยกเลิก
                        </Button>
                        <Button variant="primary" size="sm" type="submit" disabled={verifying}>
                            {verifying ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    กำลังยืนยัน...
                                </>
                            ) : (
                                "ยืนยัน"
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </VaultSessionContext.Provider>
    );
};

export const useVaultSession = () => {
    const context = useContext(VaultSessionContext);
    if (!context) {
        throw new Error('useVaultSession must be used within a VaultSessionProvider');
    }
    return context;
};
